import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { HardDrive, CheckCircle, XCircle, Link2, Unlink, Upload, ExternalLink } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

function GoogleDriveSettings() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testingUpload, setTestingUpload] = useState(false);

  useEffect(() => {
    fetchStatus();
    // Handle callback params
    if (searchParams.get('success') === 'true') {
      toast.success('Google Drive connected successfully!');
    } else if (searchParams.get('error')) {
      toast.error(`Connection failed: ${searchParams.get('error')}`);
    }
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/google-drive/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching Drive status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await api.get('/google-drive/auth');
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to start authorization');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Drive? Existing uploads will remain, but new proctoring evidence will be stored locally.')) return;
    try {
      await api.post('/google-drive/disconnect');
      toast.success('Google Drive disconnected');
      fetchStatus();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleTestUpload = async () => {
    setTestingUpload(true);
    try {
      const response = await api.post('/google-drive/test-upload');
      toast.success('Test file uploaded successfully!');
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      toast.error('Test upload failed — check your connection');
    } finally {
      setTestingUpload(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  const isConnected = status?.connected;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="gdrive-settings-title">
            Google Drive Storage
          </h1>
          <p className="text-gray-600">
            Connect Google Drive to automatically store all proctoring evidence (screenshots, violation clips, audio).
          </p>
        </div>

        {/* Connection Status */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${isConnected ? 'bg-green-100' : 'bg-gray-100'}`}>
                <HardDrive className={`w-7 h-7 ${isConnected ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Google Drive</h3>
                <div className="flex items-center gap-2 mt-1">
                  {isConnected ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700 font-medium">Connected</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">Not connected</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {isConnected ? (
              <Button variant="outline" onClick={handleDisconnect} className="text-red-600 hover:bg-red-50" data-testid="gdrive-disconnect-btn">
                <Unlink className="w-4 h-4 mr-2" />
                Disconnect
              </Button>
            ) : (
              <Button onClick={handleConnect} className="bg-blue-600 hover:bg-blue-700" data-testid="gdrive-connect-btn">
                <Link2 className="w-4 h-4 mr-2" />
                Connect Google Drive
              </Button>
            )}
          </div>
        </Card>

        {/* Connection Details */}
        {isConnected && status?.drive_info && (
          <Card className="p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">Connection Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Account</p>
                <p className="font-medium">{status.drive_info.name}</p>
              </div>
              <div>
                <p className="text-gray-500">Email</p>
                <p className="font-medium">{status.drive_info.email}</p>
              </div>
              <div>
                <p className="text-gray-500">Connected At</p>
                <p className="font-medium">
                  {status.connected_at ? new Date(status.connected_at).toLocaleString('en-IN') : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Auto-Refresh</p>
                <p className={`font-medium ${status.has_refresh_token ? 'text-green-700' : 'text-red-600'}`}>
                  {status.has_refresh_token ? 'Enabled (stays connected)' : 'Disabled (will expire)'}
                </p>
              </div>
              {status.last_refreshed_at && (
              <div>
                <p className="text-gray-500">Last Token Refresh</p>
                <p className="font-medium">
                  {new Date(status.last_refreshed_at).toLocaleString('en-IN')}
                </p>
              </div>
              )}
              <div>
                <p className="text-gray-500">Target Folder</p>
                <a
                  href={`https://drive.google.com/drive/folders/${status.folder_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                >
                  Open Folder <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </Card>
        )}

        {/* Test Upload */}
        {isConnected && (
          <Card className="p-6">
            <h3 className="font-semibold text-gray-900 mb-2">Test Connection</h3>
            <p className="text-sm text-gray-600 mb-4">Upload a small test file to verify the connection is working properly.</p>
            <Button variant="outline" onClick={handleTestUpload} disabled={testingUpload} data-testid="gdrive-test-upload-btn">
              <Upload className="w-4 h-4 mr-2" />
              {testingUpload ? 'Uploading...' : 'Test Upload'}
            </Button>
          </Card>
        )}

        {/* How it works */}
        <Card className="p-6 bg-blue-50 border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-3">How it Works</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <Badge className="bg-blue-200 text-blue-900 mt-0.5 text-xs shrink-0">1</Badge>
              <span>Connect your Google Drive account using OAuth (super admin only)</span>
            </li>
            <li className="flex items-start gap-2">
              <Badge className="bg-blue-200 text-blue-900 mt-0.5 text-xs shrink-0">2</Badge>
              <span>All proctoring evidence is automatically uploaded during tests</span>
            </li>
            <li className="flex items-start gap-2">
              <Badge className="bg-blue-200 text-blue-900 mt-0.5 text-xs shrink-0">3</Badge>
              <span>Files are organized by date, test name, and student name</span>
            </li>
            <li className="flex items-start gap-2">
              <Badge className="bg-blue-200 text-blue-900 mt-0.5 text-xs shrink-0">4</Badge>
              <span>Folder structure: <code className="bg-white px-1 rounded">HIMT Proctoring / 2026-04-21 / Test Name / Student Name / screenshots & violations</code></span>
            </li>
          </ul>
        </Card>
      </div>
    </Layout>
  );
}

export default GoogleDriveSettings;
